import { useState, useEffect, useRef, Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

interface SafetyLockDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  datasourceName: string
}

export function SafetyLockDialog({ isOpen, onClose, onConfirm, datasourceName }: SafetyLockDialogProps) {
  const [randomDigits, setRandomDigits] = useState<string>('')
  const [userInput, setUserInput] = useState<string>('')
  const [error, setError] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Generate random 3-digit code when dialog opens
  useEffect(() => {
    if (isOpen) {
      const digits = Array.from({ length: 3 }, () => Math.floor(Math.random() * 10)).join('')
      setRandomDigits(digits)
      setUserInput('   ')  // Initialize with 3 spaces
      setError(false)
      // Focus first input after a brief delay
      setTimeout(() => {
        inputRefs.current[0]?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleInputChange = (index: number, value: string) => {
    // Only allow single digits
    if (value.length > 1 || (value && !/^\d$/.test(value))) return

    // Ensure we have a 3-character string to work with
    const currentInput = userInput.padEnd(3, ' ')
    const newInput = currentInput.split('')
    newInput[index] = value || ' '
    const newInputString = newInput.join('')
    setUserInput(newInputString)
    setError(false)

    // Auto-focus next input
    if (value && index < 2) {
      inputRefs.current[index + 1]?.focus()
    }

    // Check if all digits are entered
    if (newInputString.trim().length === 3) {
      if (newInputString.trim() === randomDigits) {
        setTimeout(() => {
          onConfirm()
        }, 200)
      } else {
        setError(true)
        // Clear inputs after showing error
        setTimeout(() => {
          setUserInput('   ')  // Reset to 3 spaces
          inputRefs.current[0]?.focus()
        }, 500)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    const currentChar = userInput[index] || ' '
    if (e.key === 'Backspace') {
      e.preventDefault()
      // Clear current input
      const currentInput = userInput.padEnd(3, ' ')
      const newInput = currentInput.split('')
      newInput[index] = ' '
      setUserInput(newInput.join(''))
      
      // Move to previous input if current is empty and we're not at the first input
      if (currentChar === ' ' && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 2) {
      e.preventDefault()
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 3)
    if (/^\d+$/.test(pastedData)) {
      setUserInput(pastedData.padEnd(3, ' '))
      if (pastedData === randomDigits) {
        setTimeout(() => {
          onConfirm()
        }, 200)
      } else {
        setError(true)
        setTimeout(() => {
          setUserInput('   ')  // Reset to 3 spaces
          inputRefs.current[0]?.focus()
        }, 500)
      }
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                      <LockClosedIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <div>
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 dark:text-white">
                      Production Database Lock
                    </Dialog.Title>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      This datasource is locked for safety
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                    <div className="flex items-start space-x-2">
                      <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <p className="font-medium">Safety Verification Required</p>
                        <p className="mt-1">
                          You are about to connect to <span className="font-semibold">"{datasourceName}"</span>.
                          Enter the verification code to proceed.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Enter this code to unlock:
                    </p>
                    
                    <div className="flex justify-center space-x-3 mb-6">
                      {randomDigits.split('').map((digit, index) => (
                        <div
                          key={index}
                          className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-2xl font-bold text-gray-900 dark:text-white"
                        >
                          {digit}
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-center space-x-3">
                      {[0, 1, 2].map((index) => (
                        <input
                          key={index}
                          ref={(el) => (inputRefs.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={userInput[index]?.trim() || ''}
                          onChange={(e) => { handleInputChange(index, e.target.value) }}
                          onKeyDown={(e) => { handleKeyDown(index, e) }}
                          onPaste={index === 0 ? handlePaste : undefined}
                          className={clsx(
                            "w-16 h-16 text-center text-2xl font-bold rounded-lg border-2 transition-all",
                            "focus:outline-none focus:ring-2 focus:ring-offset-2",
                            error
                              ? "border-red-500 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 animate-shake"
                              : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-blue-500"
                          )}
                        />
                      ))}
                    </div>

                    {error && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400 animate-fade-in">
                        Incorrect code. Please try again.
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}